/* eslint-disable no-console */
import { createSelectors } from '@leverege/ui-redux';
import I18N from '@leverege/i18n';
import Utils from "./Utils.js";
import Constants from "./Constants.js";
const {
  tf
} = I18N.ns('molten.groupings.GroupingsReducer');

/**
 * This is the Redux actions to work on the Groupings
 */
const EMPTY = Object.freeze({});
const initialState = {
  currentId: null,
  data: {},
  selected: {},
  aggs: {}
};

// Listeners
const listeners = new Map();
function addUpdateListener(eventTypes, listener) {
  const keys = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
  for (let n = 0; n < keys.length; n++) {
    let arr = listeners.get(keys[n]);
    if (arr == null) {
      arr = [listener];
      listeners.set(keys[n], arr);
    } else if (!arr.includes(listener)) {
      arr.push(listener);
    }
  }
  return () => {
    removeUpdateListener(eventTypes, listener);
  };
}
function removeUpdateListener(eventTypes, listener) {
  const keys = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
  for (let n = 0; n < keys.length; n++) {
    const arr = listeners.get(keys[n]);
    if (arr) {
      const index = arr.indexOf(listener);
      if (index >= 0) {
        arr.splice(index, 1);
      }
    }
  }
}
function notifyListeners(action) {
  const arr = listeners.get(action.type);
  if (arr) {
    // Get it out of redux update loop
    setTimeout(deliverEvent, 0, arr, action);
  }
}
function deliverEvent(arr, action) {
  arr.forEach(l => {
    try {
      l(action);
    } catch (err) {
      console.error(err);
    }
  });
}

// End Listeners

function ensureSelections(id, grp, selected) {
  const sections = grp.sections || [];
  const cSelections = selected[id];
  if (cSelections == null) {
    const nSelections = {
      ...selected,
      [id]: {}
    };
    const ns = nSelections[id];
    for (let n = 0; n < sections.length; n++) {
      // find initial value
      const v = sections[n].values?.find(v => v.select) || null;
      ns[sections[n].id] = v ? v.value : null;
    }
    return nSelections;
  }
  const cIds = Object.keys(cSelections);
  if (cIds.length === sections.length) {
    // are they the same?
    let same = true;
    for (let n = 0; same && n < sections.length; n++) {
      const sid = sections[n].id;
      if (cSelections[sid] === undefined) {
        same = false;
      }
    }
    if (same) {
      return selected;
    }
  }

  // rebuild selected[id]
  const nSelections = {
    ...selected,
    [id]: {}
  };
  const ns = nSelections[id];
  for (let n = 0; n < sections.length; n++) {
    const sid = sections[n].id;
    if (cSelections[sid] !== undefined) {
      ns[sid] = cSelections[sid];
    } else {
      // find initial value
      const v = sections[n].values?.find(v => v.select) || null;
      ns[sid] = v ? v.value : null;
    }
  }
  return nSelections;
}

// eslint-disable-next-line default-param-last
function groupings(state = initialState, action) {
  notifyListeners(action);
  switch (action.type) {
    case Constants.SET_GROUPING_DATA:
      {
        const {
          id,
          objRef,
          grouping
        } = action;
        if (id == null) {
          return state;
        }
        const aggs = state.aggs[id] ? state.aggs : {
          ...state.aggs,
          [id]: {}
        };
        const nState = {
          ...state,
          aggs,
          data: {
            ...state.data,
            [id]: {
              objRef,
              grouping: {
                ...grouping,
                updateTime: Date.now()
              }
            }
          },
          selected: ensureSelections(id, grouping, state.selected)
        };
        return nState;
      }
    case Constants.SET_CURRENT_GROUPING:
      {
        const {
          id: objRefOrId,
          grouping
        } = action;
        const id = Utils.getId(objRefOrId);
        const objRef = objRefOrId && typeof objRefOrId === 'object' ? objRefOrId : null;
        if (grouping == null || objRef == null) {
          return {
            ...state,
            currentId: id
          };
        }
        const aggs = state.aggs[id] ? state.aggs : {
          ...state.aggs,
          [id]: {}
        };
        const nState = {
          currentId: id,
          data: {
            ...state.data,
            [id]: {
              objRef,
              grouping: {
                ...grouping,
                updateTime: Date.now()
              }
            }
          },
          selected: ensureSelections(id, grouping, state.selected),
          aggs
        };
        return nState;
      }
    case Constants.SET_SECTION_VALUE:
      {
        const {
          sectionId,
          value,
          id: objRefOrId
        } = action;
        const id = Utils.getId(objRefOrId);
        const {
          currentId,
          selected
        } = state;
        const grpId = id || currentId;
        if (grpId == null) {
          return state;
        }
        const nState = {
          ...state,
          selected: {
            ...selected,
            [grpId]: {
              ...(selected[grpId] || EMPTY),
              [sectionId]: value
            }
          }
        };
        return nState;
      }
    case Constants.SET_SECTION_VALUES:
      {
        const {
          values,
          id: objRefOrId
        } = action;
        const {
          currentId,
          selected
        } = state;
        const id = Utils.getId(objRefOrId);
        const grpId = id || currentId;
        if (grpId == null) {
          return state;
        }
        const nState = {
          ...state,
          selected: {
            ...selected,
            [grpId]: {
              ...values
            }
          }
        };
        return nState;
      }
    case Constants.SET_GROUPING_AGG:
      {
        const {
          id: objRefOrId,
          aggName,
          agg
        } = action;
        const id = Utils.getId(objRefOrId);
        const grpId = id || state.currentId;
        if (grpId == null) {
          return state;
        }
        const time = Date.now();
        const pState = state.aggs[id] ? {
          ...(state.aggs[id][aggName] || EMPTY),
          time,
          data: agg,
          status: Constants.AGG_LOADED,
          error: null
        } : {
          time,
          data: agg,
          status: Constants.AGG_LOADED,
          error: null
        };
        const nState = {
          ...state,
          aggs: {
            ...state.aggs,
            [id]: {
              ...(state.aggs[id] || EMPTY),
              [aggName]: pState
            }
          }
        };
        return nState;
      }
    case Constants.SET_GROUPING_AGG_STATUS:
      {
        const {
          id: objRefOrId,
          status,
          aggName,
          error = null
        } = action;
        const id = Utils.getId(objRefOrId);
        const grpId = id || state.currentId;
        if (grpId == null) {
          return state;
        }
        const pState = state.aggs[id] ? {
          ...(state.aggs[id][aggName] || EMPTY),
          status,
          error
        } : {
          status,
          error
        };
        const nState = {
          ...state,
          aggs: {
            ...state.aggs,
            [id]: {
              ...(state.aggs[id] || EMPTY),
              [aggName]: pState
            }
          }
        };
        return nState;
      }
    default:
      return state;
  }
}

// GETTERS -----

function getId(state, id) {
  const unwrap = Utils.getId(id);
  return unwrap || state.currentId || null;
}

/**
 * Gets the id of the currently active grouping.
 */
function getCurrentGroupingId(state) {
  return state.currentId;
}

/**
 * Gets data for the grouping with an id. If id is null, 
 * the currentId will be used
 */
function getGroupingAndObjRef(state, grpId) {
  return state.data[getId(state, grpId)] || EMPTY;
}

/**
 * Gets data for the grouping with an id. If id is null, 
 * the currentId will be used
 */
function getObjRef(state, grpId) {
  const data = state.data[getId(state, grpId)] || EMPTY;
  return data.objRef || null;
}

/**
 * Gets data for the grouping with an id. If id is null, 
 * the currentId will be used
 */
function getGrouping(state, objRefOrId) {
  const data = state.data[getId(state, objRefOrId)] || EMPTY;
  return data.grouping || null;
}

/**
 * Returns the currently selected section value for the given
 * section. If id is null, the currently selected grouping will
 * be used. 
 */
function getSelectedSectionValue(state, sectionId, grpId) {
  const gid = getId(state, grpId);
  if (gid == null) {
    return null;
  }
  const data = state.data[gid];
  const selected = state.selected[gid];
  if (selected == null || data == null) {
    return null;
  }
  return selected[sectionId];
}

/**
 * Returns an object of { [section.id] : <value> }  for the currently 
 * active grouping's. This will always return an object 
 */
function getSelectedSectionValues(state, grpId) {
  return state.selected[getId(state, grpId)] || EMPTY;
}

/**
 * Returns the data for the the currently active grouping's specified section data. 
 */
function getSelectedSection(state, sectionId, grpId) {
  const gid = getId(state, grpId);
  const data = state.data[gid];
  const selected = state.selected[gid];
  if (selected == null || data == null) {
    return null;
  }
  return data[sectionId];
}

/**
 * Gets the aggregation data for the give grouping id. If id is null, the
 * current id will be used
 */
function getGroupingAgg(state, aggName, grpId) {
  const aggId = getId(state, grpId);
  if (aggId) {
    return state.aggs[aggId]?.[aggName]?.data || null;
  }
  return null;
}

/**
 * Gets the time the aggregation data was set for the given grouping id. If id is null, the
 * current id will be used. If no aggregation has been received, -1 is returned.
 */
function getGroupingAggTime(state, aggName, grpId) {
  const aggId = getId(state, grpId);
  if (aggId) {
    return state.aggs[aggId]?.[aggName]?.time || -1;
  }
  return -1;
}

/**
 * Gets the status of the aggregation data for the given grouping id. If id is null, the
 * current id will be used. If no aggregation has been received, Constants.AGG_NOT_LOADED is returned.
 */
function getGroupingAggStatus(state, aggName, grpId) {
  const aggId = getId(state, grpId);
  if (aggId) {
    return state.aggs[aggId]?.[aggName]?.status || Constants.AGG_NOT_LOADED;
  }
  return Constants.AGG_NOT_LOADED;
}
const selectors = createSelectors('groupings', {
  getCurrentGroupingId,
  getObjRef,
  getGrouping,
  getGroupingAndObjRef,
  getSelectedSection,
  getSelectedSectionValue,
  getSelectedSectionValues,
  getGroupingAgg,
  getGroupingAggTime,
  getGroupingAggStatus
}, true);
function createDynamicReducer() {
  return {
    id: 'molten.grouping.GroupingReducer',
    type: 'GroupingsReducer',
    name: tf('name'),
    preferredKey: 'groupings',
    reducer: groupings,
    result: selectors.setStateKey
  };
}
export default {
  createDynamicReducer,
  getObjRef: selectors.getObjRef,
  getGrouping: selectors.getGrouping,
  getGroupingAndObjRef: selectors.getGroupingAndObjRef,
  getCurrentGroupingId: selectors.getCurrentGroupingId,
  getSelectedSection: selectors.getSelectedSection,
  getSelectedSectionValue: selectors.getSelectedSectionValue,
  getSelectedSectionValues: selectors.getSelectedSectionValues,
  getGroupingAgg: selectors.getGroupingAgg,
  getGroupingAggTime: selectors.getGroupingAggTime,
  getGroupingAggStatus: selectors.getGroupingAggStatus,
  setStateKey: selectors.setStateKey,
  reducer: groupings,
  addUpdateListener,
  removeUpdateListener
};